import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    User, LogOut, Activity, History,
    MapPin, Users, Shield,
    Building, Workflow, FileText, LayoutDashboard,
    Search,
    ChevronRight
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { handleLogout } from "../auth/logout";
import { hasPermission } from "../utils/permissionsHelper";

export const BottomDock = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchTerm, setSearchTerm] = useState("");
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);
    const profileButtonRef = useRef<HTMLButtonElement>(null);
    const userInfo = localStorage.getItem('user');
    const userName = userInfo ? JSON.parse(userInfo).name : "";
    const role = userInfo ? JSON.parse(userInfo).role : "";

    const NAV_ITEMS = [
        { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", color: "bg-[#026aa7]" },
        { id: "map", label: "Geo Location", icon: MapPin, path: "/map", color: "bg-[#4a90e2]" },
        { id: "clients", label: "Sites", icon: Building, path: "/clients", permissionKey: "clients", color: "bg-[#bd5ef8]" },
        // { id: "assets", label: "Surveillances", icon: Box, path: "/asset", permissionKey: "assets", color: "bg-[#e5a93d]" },
        { id: "events", label: "Events", icon: Activity, path: "/events", permissionKey: "events", color: "bg-[#ef5350]" },
        { id: "reports", label: "Reports", icon: FileText, path: "/reports", color: "bg-[#5c8aff]" },
        { id: "integrations", label: "Integrations", icon: Workflow, path: "/integrations", permissionKey: "integrations", color: "bg-[#4db6ac]" },
        { id: "users", label: "Users", icon: Users, path: "/users", permissionKey: "users", color: "bg-[#5DA3C7]" },
        { id: "roles", label: "Roles", icon: Shield, path: "/roles", permissionKey: "roles", color: "bg-[#81c784]" },
        { id: "audit-logs", label: "Audit Logs", icon: History, path: "/audit-logs", permissionKey: "audit_logs", color: "bg-[#9575cd]" },
    ];

    const DOCK_ITEMS = useMemo(() => {
        return NAV_ITEMS.filter(item => {
            if (item.permissionKey) return hasPermission(item.permissionKey, 'read');
            return true;
        });
    }, []);

    const filteredItems = searchTerm.trim() === ""
        ? DOCK_ITEMS
        : DOCK_ITEMS.filter(item =>
            item.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const isActive = (path: string) => {
        const basePath = path.split('?')[0];
        return location.pathname.startsWith(basePath);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                profileRef.current && !profileRef.current.contains(target) &&
                profileButtonRef.current && !profileButtonRef.current.contains(target)
            ) {
                setProfileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogoutClick = () => {
        handleLogout();
        setProfileOpen(false);
    };

    return (
        <>
            <div className="hidden lg:block fixed bottom-4 left-0 right-0 mx-auto z-50 px-4 pointer-events-none">
                <div className="relative flex items-end justify-center max-w-fit mx-auto pointer-events-auto">

                    {/* The Glass Dock (Light Theme - Compact & Recognizable) */}
                    <div className="backdrop-blur-2xl bg-slate-100/90 border border-slate-300 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] rounded-2xl px-3 py-2 flex items-center gap-2">

                        {/* Nav items */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                            {filteredItems.length > 0 ? (
                                filteredItems.map(item => (
                                    <DockIcon
                                        key={item.id}
                                        item={item}
                                        active={isActive(item.path)}
                                        onClick={() => { navigate(item.path); setSearchTerm(""); }}
                                    />
                                ))
                            ) : (
                                <div className="text-[10px] text-slate-500 px-2 font-medium">No results found.</div>
                            )}
                        </div>

                        <div className="w-px h-6 bg-slate-300/80 flex-shrink-0 mx-0.5" />

                        {/* Search */}
                        <div className="relative flex-shrink-0">
                            <Search className="w-3 h-3 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                className="bg-white/80 border border-slate-300/50 rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#422568] focus:ring-4 focus:ring-[#422568]/10 w-32 transition-all"
                            />
                        </div>

                        <div className="w-px h-6 bg-slate-300/80 flex-shrink-0 mx-0.5" />

                        {/* Profile button */}
                        <div className="relative flex-shrink-0" ref={profileRef}>
                            <button
                                ref={profileButtonRef}
                                onClick={() => setProfileOpen(!profileOpen)}
                                className={`${profileOpen ? "bg-[#422568] " : "bg-[#422568]"} hover:bg-[#422568] hover:scale-110 text-white p-2 rounded-xl transition-all duration-300 shadow-lg flex-shrink-0 relative z-50`}
                            >
                                <User className="w-4 h-4" strokeWidth={2.5} />
                            </button>

                            {/* Tooltip */}
                            {!profileOpen && (
                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 
                                    px-2 py-1 rounded-md bg-slate-900 text-white text-[9px] font-bold 
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                                    pointer-events-none whitespace-nowrap z-50 shadow-xl">
                                    Profile
                                    <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 
                                        w-1.5 h-1.5 bg-slate-900 rotate-45"></div>
                                </div>
                            )}

                            {/* ── Floating Profile Panel (Centered Above Button) ── */}
                            <AnimatePresence>
                                {profileOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, x: "-50%", scale: 0.95 }}
                                        animate={{ opacity: 1, y: -12, x: "-50%", scale: 1 }}
                                        exit={{ opacity: 0, y: 10, x: "-50%", scale: 0.95 }}
                                        className="absolute bottom-full left-1/2 bg-slate-50/95 backdrop-blur-2xl border border-slate-300 rounded-3xl p-2 w-48 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] z-[60] overflow-hidden origin-bottom"
                                    >
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2.5 p-2.5 border-b border-slate-100 mb-0.5">
                                                <div className="w-8 h-8 bg-gradient-to-br from-[#422568] to-[#422568] rounded-xl flex items-center justify-center">
                                                    <User className="w-4 h-4 text-white" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-slate-900 text-xs font-bold truncate leading-tight">{userName}</p>
                                                    <p className="text-slate-400 text-[9px] font-bold uppercase tracking-wider">{role}</p>
                                                </div>
                                            </div>

                                            <button
                                                onClick={handleLogoutClick}
                                                className="flex items-center justify-between p-2 rounded-xl hover:bg-rose-50 transition-all duration-200 group"
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center group-hover:bg-rose-100 transition-colors">
                                                        <LogOut className="w-3.5 h-3.5 text-rose-600" />
                                                    </div>
                                                    <span className="text-[11px] font-bold text-slate-600 group-hover:text-rose-600">Logout</span>
                                                </div>
                                                <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-rose-300" />
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

// ── DockIcon ──────────────────────────────────────────────────────────────────
interface DockItem {
    id: string;
    label: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    path: string;
    color?: string;
}

const DockIcon = ({
    item,
    active,
    onClick,
}: {
    item: DockItem;
    active: boolean;
    onClick: () => void;
}) => (
    <div className="relative group flex-shrink-0">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 
                px-3 py-1.5 rounded-lg bg-slate-900 
                text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 pointer-events-none 
                whitespace-nowrap z-50 shadow-xl">
            {item.label}
            <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 
                  w-2 h-2 bg-slate-900 rotate-45"></div>
        </div>
        <motion.button
            onClick={onClick}
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.15, translateY: -4 }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-300 flex-shrink-0 shadow-sm
                ${item.color || "bg-slate-100"}
                ${active
                    ? "ring-2 ring-[#422568] shadow-lg scale-110 z-10 brightness-110"
                    : "hover:brightness-110"
                }`}
        >
            <item.icon className={`w-4 h-4 text-white ${active ? "drop-shadow-md" : "opacity-90"}`} strokeWidth={2.5} />
        </motion.button>
    </div>
);

